/**
 * The creature.
 *
 * Built entirely from primitives — a gaunt, elongated humanoid twelve metres
 * tall with a pair of horn speakers where a head should be. It is deliberately
 * low-detail: it is almost always seen as a silhouette through fog, at which
 * point proportion and movement do all the work and surface detail does none.
 *
 * The entity is named generically throughout the codebase so the design can be
 * reskinned without touching game logic.
 */

import * as THREE from 'three';
import { MONSTER, MonsterState as Fsm, clamp01, damp } from '@static-pines/shared';

const FLESH = 0x2a2b28;
const FLESH_DARK = 0x1d1e1c;
const METAL = 0x55585a;

export class MonsterModel {
  readonly group = new THREE.Group();

  private readonly torso: THREE.Object3D;
  private readonly head: THREE.Object3D;
  private readonly legs: THREE.Object3D[] = [];
  private readonly arms: THREE.Object3D[] = [];
  private readonly hornLights: THREE.PointLight[] = [];
  /** Horn material, kept separate so only the sirens glow when he sounds off. */
  private readonly hornMat: THREE.MeshLambertMaterial;
  /**
   * Lights him from within during a confrontation.
   *
   * Without this he is a near-black shape in a near-black forest and the
   * forced look reveals nothing — which defeats the whole point of taking
   * control away from the player.
   */
  private readonly revealLight: THREE.PointLight;
  private revealTimer = 0;

  private walkPhase = 0;
  private lean = 0;
  /** Smoothed position so 20 Hz server updates render as continuous motion. */
  private renderX = 0;
  private renderY = 0;
  private renderZ = 0;
  private renderYaw = 0;
  private initialised = false;

  constructor() {
    this.group.name = 'monster';

    const bodyMat = new THREE.MeshLambertMaterial({ color: FLESH });
    const darkMat = new THREE.MeshLambertMaterial({ color: FLESH_DARK });
    const metalMat = new THREE.MeshLambertMaterial({ color: METAL });
    this.hornMat = new THREE.MeshLambertMaterial({
      color: METAL,
      emissive: new THREE.Color(0x000000),
    });

    const scale = MONSTER.HEIGHT / 12;

    // --- Torso: long, thin, slightly hunched --------------------------------
    this.torso = new THREE.Group();
    this.torso.position.y = 7.2 * scale;
    this.group.add(this.torso);

    const chest = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.5, 3.4, 7), bodyMat);
    chest.position.y = 0.4;
    this.torso.add(chest);

    const hips = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.58, 1.2, 7), darkMat);
    hips.position.y = -1.7;
    this.torso.add(hips);

    // --- Head: the twin horn speakers ---------------------------------------
    this.head = new THREE.Group();
    this.head.position.y = 2.4;
    this.torso.add(this.head);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 1.0, 6), darkMat);
    this.head.add(neck);

    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 2.0, 6), metalMat);
    bar.rotation.z = Math.PI / 2;
    bar.position.y = 0.6;
    this.head.add(bar);

    for (const side of [-1, 1]) {
      // Cone horns facing outward and slightly forward.
      const horn = new THREE.Mesh(new THREE.ConeGeometry(0.78, 1.9, 9, 1, true), this.hornMat);
      horn.rotation.z = (side * Math.PI) / 2;
      horn.rotation.y = -side * 0.25;
      horn.position.set(side * 1.5, 0.6, 0);
      this.head.add(horn);

      const throat = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.3, 0.7, 6), darkMat);
      throat.rotation.z = (side * Math.PI) / 2;
      throat.position.set(side * 0.65, 0.6, 0);
      this.head.add(throat);

      // The horns glow when he sounds off. Off by default.
      // Intensity is large because falloff is quadratic and the head sits
      // twelve metres up — a "reasonable" value produces nothing at all.
      const light = new THREE.PointLight(0xffcf7a, 0, 90, 2);
      light.position.set(side * 2.1, 0.6, 0);
      light.visible = false;
      this.head.add(light);
      this.hornLights.push(light);
    }

    // Lights his own body during a confrontation. Placed well *in front* of
    // him: a light at his centre leaves every camera-facing surface with its
    // normal pointing away from it, so he renders as a black cut-out.
    this.revealLight = new THREE.PointLight(0xffd9a0, 0, 34, 2);
    this.revealLight.position.set(0, 0.4, 5.5);
    this.revealLight.visible = false;
    this.torso.add(this.revealLight);

    // --- Limbs: absurdly long, which is most of the silhouette ---------------
    for (const side of [-1, 1]) {
      const arm = new THREE.Group();
      arm.position.set(side * 0.66, 1.5, 0);
      this.torso.add(arm);
      this.arms.push(arm);

      const upper = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.14, 3.0, 5), bodyMat);
      upper.position.y = -1.5;
      arm.add(upper);

      const fore = new THREE.Group();
      fore.position.y = -3.0;
      arm.add(fore);

      const foreArm = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.1, 3.2, 5), bodyMat);
      foreArm.position.y = -1.6;
      fore.add(foreArm);

      // Splayed fingers — what actually picks a player up.
      for (let f = 0; f < 4; f++) {
        const finger = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.03, 1.1, 4), darkMat);
        finger.position.set((f - 1.5) * 0.14, -3.6, 0);
        finger.rotation.x = 0.2 + f * 0.08;
        fore.add(finger);
      }

      const leg = new THREE.Group();
      leg.position.set(side * 0.34, -2.2, 0);
      this.torso.add(leg);
      this.legs.push(leg);

      const thigh = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.18, 2.9, 5), bodyMat);
      thigh.position.y = -1.45;
      leg.add(thigh);

      const shin = new THREE.Group();
      shin.position.y = -2.9;
      leg.add(shin);

      const calf = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.12, 2.9, 5), bodyMat);
      calf.position.y = -1.45;
      shin.add(calf);

      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.2, 1.0), darkMat);
      foot.position.set(0, -2.95, 0.22);
      shin.add(foot);
    }

    this.group.scale.setScalar(scale);
    this.group.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) o.castShadow = true;
    });

    this.group.visible = false;
  }

  /**
   * Light him up for the duration of a siren blast.
   *
   * The horns become self-lit and throw real light, and a fill light inside
   * the torso makes his body readable. Taking the player's control away to
   * show them nothing would be worse than not taking it at all.
   */
  flashHorns(duration = 2.5): void {
    this.revealTimer = duration;

    this.hornMat.emissive.setHex(0xffb347);
    for (const light of this.hornLights) {
      light.visible = true;
      light.intensity = 420;
    }
    this.revealLight.visible = true;
    this.revealLight.intensity = 140;
  }

  /**
   * @param x,y,z,yaw  latest authoritative transform
   * @param speed      current movement speed, drives the gait
   * @param fsm        state, drives posture
   */
  update(
    dt: number,
    x: number,
    y: number,
    z: number,
    yaw: number,
    speed: number,
    fsm: number,
    visible: boolean,
  ): void {
    // Decayed before the visibility check so a reveal cannot get stuck on if
    // he slips out of view mid-flash.
    this.updateReveal(dt);

    this.group.visible = visible;
    if (!visible) return;

    if (!this.initialised) {
      this.renderX = x;
      this.renderY = y;
      this.renderZ = z;
      this.renderYaw = yaw;
      this.initialised = true;
    }

    // Smooth the 20 Hz state stream into continuous motion.
    this.renderX = damp(this.renderX, x, 12, dt);
    this.renderY = damp(this.renderY, y, 12, dt);
    this.renderZ = damp(this.renderZ, z, 12, dt);

    // Wrap-aware yaw smoothing.
    let dYaw = yaw - this.renderYaw;
    while (dYaw > Math.PI) dYaw -= Math.PI * 2;
    while (dYaw < -Math.PI) dYaw += Math.PI * 2;
    this.renderYaw += dYaw * Math.min(1, dt * 9);

    this.group.position.set(this.renderX, this.renderY, this.renderZ);
    this.group.rotation.y = this.renderYaw;

    // Gait. Stride frequency follows speed so a charge reads as a charge.
    const stride = speed > 0.1 ? speed / 3.2 : 0;
    this.walkPhase += dt * stride * 2.2;

    const swing = Math.sin(this.walkPhase);
    const swingB = Math.sin(this.walkPhase + Math.PI);

    this.legs[0]!.rotation.x = swing * 0.55;
    this.legs[1]!.rotation.x = swingB * 0.55;
    this.arms[0]!.rotation.x = swingB * 0.38;
    this.arms[1]!.rotation.x = swing * 0.38;

    // Hunt and chase bring him forward into a stalk; roaming stands tall.
    const targetLean = fsm >= Fsm.Chase ? 0.34 : fsm >= Fsm.Hunt ? 0.18 : 0.04;
    this.lean = damp(this.lean, targetLean, 3, dt);
    this.torso.rotation.x = this.lean;

    // A slow, wrong sway. Nothing this size should move this smoothly.
    this.torso.rotation.z = Math.sin(this.walkPhase * 0.5) * 0.05;
    this.head.rotation.y = Math.sin(this.walkPhase * 0.31) * 0.22;
  }

  private updateReveal(dt: number): void {
    if (this.revealTimer <= 0) return;

    this.revealTimer -= dt;

    if (this.revealTimer <= 0) {
      this.hornMat.emissive.setHex(0x000000);
      for (const light of this.hornLights) {
        light.visible = false;
        light.intensity = 0;
      }
      this.revealLight.visible = false;
      this.revealLight.intensity = 0;
      return;
    }

    // Fade out over the last stretch, with an unsteady flicker — a working
    // siren, not a stage light.
    const fade = clamp01(this.revealTimer / 0.7);
    const flicker = 0.82 + Math.sin(this.walkPhase * 41) * 0.18;

    for (const light of this.hornLights) light.intensity = 420 * fade * flicker;
    this.revealLight.intensity = 140 * fade;
    this.hornMat.emissive.setRGB(1.0 * fade, 0.7 * fade, 0.28 * fade);
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }
}
